import { IContext } from "@src/context";
import duDatepicker from "@dmuy/datepicker";
import { getEnumByLocale } from "@src/utils/languageHelper";
import { getInitialAccessUrl } from "@src/utils/initialAccessUrl";
import {
  saveSubmissionToSessionStorage,
  generateRecordId,
} from "@src/analytics";
import {
  isFavorite,
  addFavoriteItem,
  removeFavoriteItem,
} from "@src/utils/favorite";
import {
  isValidEmail,
  isValidTelephone,
  isValidName,
  isKatakanaChar,
} from "@src/utils/checker";
import { isOnlySpaces } from "@src/autoDebit/components/Register/isValid2ByteCharacters";
import {
  parseDateFromString,
  apartmentVisitsText,
} from "@src/utils/availableDate";
import { isOfficeHours } from "@src/utils/officeHours";
import messages from "./messages";

import "./index.css";

const isDesktop = window.matchMedia("screen and (min-width: 768px)").matches;

export function setupBookVisit(context: IContext) {
  const { intl } = context;

  const container = document.querySelector(".container-book-visit");

  if (container == null) {
    return;
  }

  const isJapanese = context.locale === "ja";

  // update working hours hint
  const isOfficeHoursNow = isOfficeHours(context?.locale);
  if (isOfficeHoursNow) {
    container.querySelector("[data-message='offline']")?.remove();
    // flag online
    container
      .querySelector(".container-book-visit-head")
      ?.setAttribute("data-online", "");
  } else {
    container.querySelector("[data-message='online']")?.remove();
  }

  // toggle favorite
  const favoriteBtn = container.querySelector(
    ".container-book-visit-info-favorite"
  );
  container.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const btn = event.target.closest(".container-book-visit-info-favorite");
      const propertyId = btn?.getAttribute("data-property-id");
      if (btn && propertyId) {
        if (btn.getAttribute("data-favorite") === "true") {
          // remove from favorite
          btn.setAttribute("data-favorite", "false");
          removeFavoriteItem(propertyId);
        } else {
          // add to favorite
          btn.setAttribute("data-favorite", "true");
          addFavoriteItem(propertyId);
        }
      }
    }
  });

  // clear datepicker input
  container.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const clear = event.target.closest(
        ".container-book-visit-datepicker-clear"
      );
      if (clear) {
        const datapicker = clear.parentElement?.querySelector(
          ".container-book-visit-datepicker"
        );
        duDatepicker(datapicker, "setValue", "");
      }
    }
  });

  // confirm email can only be typed, not pasted
  const confirmEmailInput = container.querySelector(
    "input[name='confirm_email']"
  );
  confirmEmailInput?.addEventListener("paste", (e) => {
    e.preventDefault();
  });

  // Room Picker
  const roomPicker = container.querySelector(
    ".container-book-visit-info-picker"
  );
  const roomPickerShow = container.querySelector(
    ".container-book-visit-info-picker-show"
  );
  const roomPickerDropdown = container.querySelector(
    ".container-book-visit-info-picker-dropdown"
  );
  const roomPickerDropdownClose = container.querySelector(
    ".container-book-visit-info-picker-dropdown-close"
  );
  const roomPickerDropdownList = container.querySelector(
    ".container-book-visit-info-picker-dropdown-list"
  );
  const roomPickerDropdownButton = container.querySelector(
    ".container-book-visit-info-picker-dropdown-button"
  );

  // disable popup button if no room
  if (roomPickerDropdownList?.querySelectorAll("li").length === 0) {
    document
      .querySelector(".container-contact-bottom-book")
      ?.setAttribute("disabled", "");
  }

  // set active room
  document.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const button = event.target.closest(
        '[data-popup-id="book-visit"][data-popup-event="popup-open"]'
      );
      if (button) {
        if (button.hasAttribute("data-property-id")) {
          const propertyId = button.getAttribute("data-property-id");
          roomPicker?.setAttribute("data-property-id", propertyId || "");
          // update favorite
          updateFavoriteBtn(propertyId);
        }
        if (button.hasAttribute("data-room")) {
          const room = button.getAttribute("data-room");
          pickerRoom(room || "");
          roomPickerDropdown?.removeAttribute("data-show");
        } else {
          pickerRoom("");
          roomPickerDropdown?.setAttribute("data-show", "");
        }
      }
    }
  });

  roomPickerShow?.addEventListener("click", (e) => {
    e.preventDefault();
    roomPickerDropdown?.setAttribute("data-show", "");
  });
  roomPickerDropdownClose?.addEventListener("click", (e) => {
    e.preventDefault();
    roomPickerDropdown?.removeAttribute("data-show");
    // reset selected
    roomPickerDropdown?.querySelectorAll("li[data-selected]").forEach((li) => {
      li.removeAttribute("data-selected");
    });
    const activeRoom = roomPicker?.getAttribute("data-room");
    if (activeRoom) {
      roomPickerDropdown
        ?.querySelector(`li[data-room="${activeRoom}"]`)
        ?.setAttribute("data-selected", "");
    }
  });
  roomPickerDropdownList?.addEventListener("click", (e) => {
    e.preventDefault();
    if (e.target instanceof HTMLElement) {
      const li = e.target.closest("li[data-room]");
      if (li) {
        roomPickerDropdown
          ?.querySelectorAll("li[data-selected]")
          .forEach((li) => {
            li.removeAttribute("data-selected");
          });
        li.setAttribute("data-selected", "");
      }
    }
  });
  roomPickerDropdownButton?.addEventListener("click", (e) => {
    e.preventDefault();
    const li = roomPickerDropdown?.querySelector("li[data-selected]");
    if (li) {
      const room = li.getAttribute("data-room");
      pickerRoom(room || "");
      roomPickerDropdown?.removeAttribute("data-show");
    }
  });

  // toggle policy checkbox
  container
    .querySelector(
      ".container-book-visit-policy .container-enquiry-form-checkbox"
    )
    ?.addEventListener("change", (e) => {
      const checkbox = e.target as HTMLInputElement;
      const submit = container.querySelector(
        ".container-book-visit-form-submit"
      ) as HTMLButtonElement;
      submit.disabled = !checkbox.checked;
    });

  // add reCAPTCHA script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.google.com/recaptcha/api.js?render=${
    import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY
  }`;
  document.body.appendChild(script);

  function getTime1Field() {
    let time1FieldValue = "";
    const time1Field = container?.querySelector(
      ".container-book-visit-form-field[data-field='time1']"
    );
    const time1Datepicker = time1Field?.querySelector(
      ".container-book-visit-datepicker"
    );
    const dateError = time1Field?.querySelector(
      ".container-book-visit-form-field-input .container-book-visit-error"
    ) as HTMLElement;
    if (!time1Datepicker?.getAttribute("value")) {
      dateError.innerHTML = intl.formatMessage(messages.requiredDesiredDate);
      dateError?.setAttribute("data-show", "");
    } else {
      dateError?.removeAttribute("data-show");
    }
    const time1RatioChecked = time1Field?.querySelector(
      ".container-book-visit-form-field-time input[type='radio']:checked"
    );
    if (isDesktop) {
      const ratioError = time1Field?.querySelector(
        ".container-book-visit-form-field-time .container-book-visit-error"
      ) as HTMLElement;
      if (!time1RatioChecked) {
        ratioError.innerHTML = intl.formatMessage(messages.requiredDesiredTime);
        ratioError?.setAttribute("data-show", "");
      } else {
        ratioError?.removeAttribute("data-show");
        time1FieldValue =
          time1Datepicker?.getAttribute("value") +
          " " +
          time1RatioChecked?.getAttribute("value");
      }
    } else if (time1Datepicker?.getAttribute("value")) {
      const time1Select = time1Field?.querySelector(
        ".container-book-visit-select"
      ) as HTMLSelectElement;
      time1FieldValue =
        time1Datepicker?.getAttribute("value") +
        " " +
        time1Select?.options[time1Select?.selectedIndex].value;
    }
    return time1FieldValue;
  }
  function getTime2Field() {
    let time2FieldValue = "";
    const time2Field = container?.querySelector(
      ".container-book-visit-form-field[data-field='time2']"
    );
    const time2Datepicker = time2Field?.querySelector(
      ".container-book-visit-datepicker"
    );
    const dateError = time2Field?.querySelector(
      ".container-book-visit-form-field-input .container-book-visit-error"
    ) as HTMLElement;
    if (!time2Datepicker?.getAttribute("value")) {
      dateError.innerHTML = intl.formatMessage(messages.requiredDesiredDate);
      dateError?.setAttribute("data-show", "");
    } else {
      dateError?.removeAttribute("data-show");
    }
    const time2RatioChecked = time2Field?.querySelector(
      ".container-book-visit-form-field-time input[type='radio']:checked"
    );
    if (isDesktop) {
      const ratioError = time2Field?.querySelector(
        ".container-book-visit-form-field-time .container-book-visit-error"
      ) as HTMLElement;
      if (!time2RatioChecked) {
        ratioError.innerHTML = intl.formatMessage(messages.requiredDesiredTime);
        ratioError?.setAttribute("data-show", "");
      } else {
        ratioError?.removeAttribute("data-show");
        time2FieldValue =
          time2Datepicker?.getAttribute("value") +
          " " +
          time2RatioChecked?.getAttribute("value");
      }
    } else if (time2Datepicker?.getAttribute("value")) {
      const time1Select = time2Field?.querySelector(
        ".container-book-visit-select"
      ) as HTMLSelectElement;
      time2FieldValue =
        time2Datepicker?.getAttribute("value") +
        " " +
        time1Select?.options[time1Select?.selectedIndex].value;
    }
    return time2FieldValue;
  }
  function getTIme3Field() {
    let time3FieldValue = "";
    const time3Field = container?.querySelector(
      ".container-book-visit-form-field[data-field='time3']"
    );
    const time3Datepicker = time3Field?.querySelector(
      ".container-book-visit-datepicker"
    );
    if (time3Datepicker?.getAttribute("value")) {
      const time3RatioChecked = time3Field?.querySelector(
        ".container-book-visit-form-field-time input[type='radio']:checked"
      );
      if (isDesktop) {
        const ratioError = time3Field?.querySelector(
          ".container-book-visit-form-field-time .container-book-visit-error"
        ) as HTMLElement;
        if (!time3RatioChecked) {
          ratioError.innerHTML = intl.formatMessage(
            messages.requiredDesiredTime
          );
          ratioError?.setAttribute("data-show", "");
        } else {
          ratioError?.removeAttribute("data-show");
          time3FieldValue =
            time3Datepicker?.getAttribute("value") +
            " " +
            time3RatioChecked?.getAttribute("value");
        }
      } else if (time3Datepicker?.getAttribute("value")) {
        const time3Select = time3Field?.querySelector(
          ".container-book-visit-select"
        ) as HTMLSelectElement;
        time3FieldValue =
          time3Datepicker?.getAttribute("value") +
          " " +
          time3Select?.options[time3Select?.selectedIndex].value;
      }
    }
    return time3FieldValue;
  }
  function getNameField() {
    const nameField = container?.querySelector(
      ".container-book-visit-form-field[data-field='name']"
    );
    const nameInput = nameField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const nameError = nameField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!nameInput?.value) {
      nameError.innerHTML = intl.formatMessage(messages.requiredField);
      nameError?.setAttribute("data-show", "");
    } else if (!isValidName(nameInput?.value)) {
      nameError.innerHTML = intl.formatMessage(messages.invalidName);
      nameError?.setAttribute("data-show", "");
    } else {
      nameError?.removeAttribute("data-show");
      return nameInput?.value;
    }
    return "";
  }
  function getJapaneseNameField() {
    const japaneseNameField = container?.querySelector(
      ".container-book-visit-form-field[data-field='name_ja']"
    );
    const japaneseNameInput = japaneseNameField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const japaneseNameError = japaneseNameField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!japaneseNameInput?.value) {
      japaneseNameError.innerHTML = intl.formatMessage(messages.requiredField);
      japaneseNameError?.setAttribute("data-show", "");
    } else if (!isValidName(japaneseNameInput?.value)) {
      japaneseNameError.innerHTML = intl.formatMessage(
        messages.invalidJapaneseName
      );
      japaneseNameError?.setAttribute("data-show", "");
    } else {
      japaneseNameError?.removeAttribute("data-show");
      return japaneseNameInput?.value;
    }
    return "";
  }
  function getKanaNameField() {
    const kanaNameField = container?.querySelector(
      ".container-book-visit-form-field[data-field='name_kana']"
    );
    const kanaNameInput = kanaNameField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const kanaNameError = kanaNameField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!kanaNameInput?.value) {
      kanaNameError.innerHTML = intl.formatMessage(messages.requiredField);
      kanaNameError?.setAttribute("data-show", "");
    } else if (
      !isKatakanaChar(kanaNameInput?.value) ||
      isOnlySpaces(kanaNameInput?.value)
    ) {
      kanaNameError.innerHTML = intl.formatMessage(messages.invalidKanaName);
      kanaNameError?.setAttribute("data-show", "");
    } else {
      kanaNameError?.removeAttribute("data-show");
      return kanaNameInput?.value;
    }
    return "";
  }
  function getTelephoneField() {
    const telephoneField = container?.querySelector(
      ".container-book-visit-form-field[data-field='telephone']"
    );
    const telephoneInput = telephoneField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const telephoneError = telephoneField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!telephoneInput?.value) {
      telephoneError.innerHTML = intl.formatMessage(messages.requiredField);
      telephoneError?.setAttribute("data-show", "");
    } else if (!isValidTelephone(telephoneInput?.value)) {
      telephoneError.innerHTML = intl.formatMessage(messages.invaildTelephone);
      telephoneError?.setAttribute("data-show", "");
    } else {
      telephoneError?.removeAttribute("data-show");
    }
    return telephoneInput?.value;
  }
  function getEmailField() {
    const emailField = container?.querySelector(
      ".container-book-visit-form-field[data-field='email']"
    );
    const emailInput = emailField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const emailError = emailField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!emailInput?.value) {
      emailError.innerHTML = intl.formatMessage(messages.requiredField);
      emailError?.setAttribute("data-show", "");
    } else if (!isValidEmail(emailInput?.value)) {
      emailError.innerHTML = intl.formatMessage(messages.invalidEmail);
      emailError?.setAttribute("data-show", "");
    } else {
      emailError?.removeAttribute("data-show");
    }
    return emailInput?.value;
  }
  function getConfirmEmailField() {
    const confirmEmailField = container?.querySelector(
      ".container-book-visit-form-field[data-field='confirm_email']"
    );
    const emailInput = confirmEmailField?.querySelector(
      ".container-book-visit-input"
    ) as HTMLInputElement;
    const emailError = confirmEmailField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!emailInput?.value) {
      emailError.innerHTML = intl.formatMessage(messages.requiredField);
      emailError?.setAttribute("data-show", "");
    } else if (!isValidEmail(emailInput?.value)) {
      emailError.innerHTML = intl.formatMessage(messages.invalidEmail);
      emailError?.setAttribute("data-show", "");
    } else if (emailInput?.value !== email) {
      emailError.innerHTML = intl.formatMessage(messages.invalidConfirmEmail);
      emailError?.setAttribute("data-show", "");
    } else {
      emailError?.removeAttribute("data-show");
    }
    return emailInput?.value;
  }
  function getModeOfTransportationField() {
    const modeOfTransportationField = container?.querySelector(
      ".container-book-visit-form-field[data-field='mode_of_transportation']"
    );
    const modeOfTransportationSelect = modeOfTransportationField?.querySelector(
      ".container-book-visit-select"
    ) as HTMLInputElement;
    return modeOfTransportationSelect?.value;
  }
  function getJapaneseAttendField() {
    const japaneseAttendField = container?.querySelector(
      ".container-book-visit-form-field[data-field='japanese_attend']"
    );
    const japaneseAttendRatioChecked = japaneseAttendField?.querySelector(
      "input[type='radio']:checked"
    );
    const japaneseAttendError = japaneseAttendField?.querySelector(
      ".container-book-visit-error"
    ) as HTMLElement;
    if (!japaneseAttendRatioChecked) {
      japaneseAttendError.innerHTML = intl.formatMessage(messages.required);
      japaneseAttendError?.setAttribute("data-show", "");
    } else {
      japaneseAttendError?.removeAttribute("data-show");
      return japaneseAttendRatioChecked?.getAttribute("value") === "1"
        ? "yes"
        : "no";
    }
    return "";
  }

  let time1FieldValue = "";
  let time2FieldValue = "";
  let time3FieldValue = "";
  let name = "";
  let kanaName = "";
  let japaneseAttend = "";
  let telephone = "";
  let email = "";
  let confirmEmail = "";
  let modeOfTransportation = "";

  function checkAllFields() {
    // check fields valid
    time1FieldValue = getTime1Field();
    time2FieldValue = getTime2Field();
    time3FieldValue = getTIme3Field();
    if (!isJapanese) {
      // check name field required
      name = getNameField();
    }
    if (isJapanese) {
      // check japanese name field required
      name = getJapaneseNameField();
      // check kana name field required
      kanaName = getKanaNameField();
    }
    // check telephone field required
    telephone = getTelephoneField();
    // check email field required
    email = getEmailField();
    // check confirm email field required
    confirmEmail = getConfirmEmailField();
    // check mode of transportation field required
    modeOfTransportation = getModeOfTransportationField();

    if (!isJapanese) {
      // check japanese attend field required
      japaneseAttend = getJapaneseAttendField();
    }
  }

  // submit
  const submitBtn = container.querySelector(
    ".container-book-visit-form-submit"
  );
  let checked = false;
  let submited = false;

  submitBtn?.addEventListener("click", (e) => {
    e.preventDefault();

    setSubmitError(false);

    checkAllFields();
    if (!checked) {
      container?.addEventListener("input", (e) => {
        if (e.target instanceof HTMLElement) {
          const field = e.target.closest("[data-field]");
          if (field) {
            checkAllFields();
          }
        }
      });
      container?.addEventListener("change", (e) => {
        if (e.target instanceof HTMLElement) {
          const field = e.target.closest("[data-field]");
          if (field) {
            checkAllFields();
          }
        }
      });
      checked = true;
    }

    if (
      container?.querySelectorAll(".container-book-visit-error[data-show]")
        .length > 0 ||
      submited
    ) {
      return;
    }

    // get data
    const propertyId = roomPicker?.getAttribute("data-property-id");
    const roomId = roomPicker?.getAttribute("data-room");
    const messageInput = container.querySelector(
      ".container-book-visit-form-field[data-field='message'] .container-book-visit-textarea"
    ) as HTMLTextAreaElement;

    const form = {
      lang: getEnumByLocale(context.locale),
      ...(propertyId ? { property_id: Number(propertyId) } : {}),
      ...(roomId ? { room_id: roomId } : {}),
      name,
      ...(kanaName ? { name_kana: kanaName } : {}),
      email,
      confirm_email_address: confirmEmail,
      telephone,
      message: messageInput?.value || "",
      current_url: window.location.href,
      initial_access_url: getInitialAccessUrl(),
      visit_date_1: time1FieldValue,
      visit_date_2: time2FieldValue,
      ...(time3FieldValue ? { visit_date_3: time3FieldValue } : {}),
      mode_of_transportation: modeOfTransportation,
      ...(japaneseAttend ? { japanese_attend: japaneseAttend === "yes" } : {}),
      record_id: generateRecordId(),
    };

    submited = true;

    const baseUrl = document.body.getAttribute("data-site-domain") || "";
    const apiUrl = `${baseUrl}/vhmformapi.bookvisit.BookVisitFormService/SubmitBooking`;

    // add reCAPTCHA token
    grecaptcha.ready(function () {
      grecaptcha
        .execute(import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY, {
          action: "submit",
        })
        .then(function (token) {
          window
            .fetch(apiUrl, {
              credentials: "include",
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "G-Recaptcha-Response": token,
              },
              body: JSON.stringify(form),
            })
            .then((response) => {
              if (response.status != 200) {
                throw new Error(
                  `Failed to call filter properties, status: ${response.status} on url ${apiUrl}`
                );
              }
              return response.json();
            })
            .then((response) => {
              // redirect to thank you page
              if (response.redirect_url) {
                // GTM push `new_appointment_submission` event when redirected
                saveSubmissionToSessionStorage({
                  eventName: "new_appointment_submission",
                  propertyId: propertyId || "",
                  name,
                  email,
                  phone: telephone,
                  recordId: form.record_id,
                });

                window.location.href = response.redirect_url;
              }
            })
            .catch((error) => {
              setSubmitError(true);
              throw error;
            })
            .finally(() => {
              submited = false;
            });
        });
    });
  });

  function initDataPicker(minDate: string, maxDate: string) {
    const datapicker1 =
      '.container-book-visit-form-field[data-field="time1"] .container-book-visit-datepicker';
    const datapicker2 =
      '.container-book-visit-form-field[data-field="time2"] .container-book-visit-datepicker';
    const datapicker3 =
      '.container-book-visit-form-field[data-field="time3"] .container-book-visit-datepicker';

    // reset value
    duDatepicker(datapicker1, "setValue", "");
    duDatepicker(datapicker2, "setValue", "");
    duDatepicker(datapicker3, "setValue", "");

    // destroy datepicker
    duDatepicker(datapicker1, "destroy");
    duDatepicker(datapicker2, "destroy");
    duDatepicker(datapicker3, "destroy");

    // init datepicker
    const config = {
      clearBtn: false,
      format: "yyyy/mm/dd",
      minDate,
      maxDate,
      i18n: context.locale,
      range: false,
      disabledDates: JSON.parse(
        sessionStorage?.getItem("book_visit_datepicker_disabled_dates") || "[]"
      ),
    };

    duDatepicker(datapicker1, {
      ...config,
      events: {
        dateChanged: (e: { date: string }) => {
          handleDateChange(
            e.date,
            document.querySelector(
              '.container-book-visit-form-field[data-field="time1"]'
            )
          );
        },
      },
    });
    duDatepicker(datapicker2, {
      ...config,
      events: {
        dateChanged: (e: { date: string }) => {
          handleDateChange(
            e.date,
            document.querySelector(
              '.container-book-visit-form-field[data-field="time2"]'
            )
          );
        },
      },
    });
    duDatepicker(datapicker3, {
      ...config,
      events: {
        dateChanged: (e: { date: string }) => {
          handleDateChange(
            e.date,
            document.querySelector(
              '.container-book-visit-form-field[data-field="time3"]'
            )
          );
        },
      },
    });

    // update datepicker header
    document
      .querySelectorAll(".dcalendarpicker .dudp__calendar-header")
      .forEach((header, index) => {
        const nameIndex = ["selectDate1", "selectDate2", "selectDate3"][
          index
        ] as keyof typeof messages;
        header.innerHTML = `<p>${intl.formatMessage(messages[nameIndex])}</p>`;
      });
  }

  function handleDateChange(date: string, el: HTMLElement | null) {
    // desktop
    if (el) {
      // toggle clear button
      if (date) {
        el.querySelector(
          ".container-book-visit-datepicker-clear"
        )?.setAttribute("data-show", "");
      } else {
        el.querySelector(
          ".container-book-visit-datepicker-clear"
        )?.removeAttribute("data-show");
      }
      // clear time 3
      if (el.getAttribute("data-field") === "time3") {
        el.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (date) {
            input.removeAttribute("disabled");
          } else {
            input.setAttribute("disabled", "");
            (input as HTMLInputElement).checked = false;
          }
        });
        // mobile
        el.querySelectorAll(".container-book-visit-select").forEach(
          (select) => {
            if (date) {
              select.removeAttribute("disabled");
            } else {
              select.setAttribute("disabled", "");
            }
          }
        );
      }
    }
  }

  function updateFavoriteBtn(propertyId?: string | null) {
    if (propertyId) {
      favoriteBtn?.setAttribute("data-property-id", propertyId);
      favoriteBtn?.setAttribute(
        "data-favorite",
        isFavorite(propertyId).toString()
      );
    } else {
      favoriteBtn?.removeAttribute("data-property-id");
      favoriteBtn?.removeAttribute("data-favorite");
    }
  }

  function pickerRoom(room: string) {
    const showInfo = roomPickerShow?.querySelector(
      ".container-book-visit-info-picker-show-info"
    );
    const firstRoom = roomPickerDropdown?.querySelector("li[data-room]");
    const selectRoom = room
      ? roomPickerDropdown?.querySelector(`li[data-room="${room}"]`) ||
        firstRoom
      : firstRoom;

    if (showInfo && selectRoom) {
      roomPickerDropdown
        ?.querySelectorAll("li[data-selected]")
        .forEach((li) => {
          li.removeAttribute("data-selected");
        });
      selectRoom.setAttribute("data-selected", "");
      roomPicker?.setAttribute(
        "data-room",
        selectRoom?.getAttribute("data-room") || ""
      );
      showInfo.innerHTML = selectRoom.innerHTML;

      const apartmentVisits = container?.querySelector(
        ".container-book-visit-available-date span"
      );
      if (apartmentVisits) {
        const date = parseDateFromString(
          selectRoom?.getAttribute("data-apartment-visits") || ""
        );
        apartmentVisits.innerHTML = apartmentVisitsText(context, date);

        // update datepicker range
        const threeDaysLater = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
        let minDate: string;
        let maxDate: string;

        /* Reference: https://theplanttokyo.atlassian.net/browse/VHM-752?focusedCommentId=265839
          「3 days after the date of submission」 to 「2 weeks later counting from 3 days after the date of submission」
          「the earliest available date for visit」to「2 weeks after the earliest available date for visit」
          ※If the 「earliest available date for visit」 is earlier than 「3 days after the date of submission」, then please use 「3 days after the date of submission」 */
        if (date && date > threeDaysLater) {
          minDate = date.toISOString().slice(0, 10).replaceAll("-", "/");
          date.setDate(date.getDate() + 14);
          maxDate = date.toISOString().slice(0, 10).replaceAll("-", "/"); // +14 days
        } else {
          minDate = threeDaysLater
            .toISOString()
            .slice(0, 10)
            .replaceAll("-", "/"); // +3 days
          maxDate = new Date(Date.now() + 17 * 24 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 10)
            .replaceAll("-", "/"); // +17 days
        }

        initDataPicker(minDate, maxDate);
      }
    }
  }

  function setSubmitError(show: boolean) {
    const removeError = () => {
      container?.querySelector(".container-book-visit-form-error")?.remove();
    };
    if (show) {
      removeError();
      container
        ?.querySelector(".container-book-visit-bottom")
        ?.insertAdjacentHTML(
          "afterend",
          `<div class="container-book-visit-form-error">${intl.formatMessage(
            messages.submitError
          )}</div>`
        );
    } else {
      removeError();
    }
  }
}
